import React, { useState, useEffect } from 'react';
import axios from 'axios';
import api from '../services/api';
import { useToast } from '../components/ToastContext';
import { useI18n } from '../i18n';
import './Steps.css';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function Step2Rules({ onNext, onBack, datalabPromise }) {
  const { showToast } = useToast();
  const { t, lang } = useI18n();

  const [predefinedAttachments, setPredefinedAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(false);
  const [processingDatalab, setProcessingDatalab] = useState(true);
  const [markdownFiles, setMarkdownFiles] = useState(null);
  const [datalabTime, setDatalabTime] = useState(0);
  const [formData, setFormData] = useState({
    requestId: '993022000232',
    civilId: '1020233217',
    requesterName: '',
    attachments: []
  });

  const [customAttachment, setCustomAttachment] = useState('');
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchRules = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_URL}/rules`);
        setPredefinedAttachments(response.data);
      } catch (err) {
        console.error('Error fetching rules:', err);
        // Fallback to empty or some defaults if API fails
      } finally {
        setLoading(false);
      }
    };
    fetchRules();
  }, []);

  // Wait for Datalab API to complete
  useEffect(() => {
    if (datalabPromise) {
      datalabPromise.then((result) => {
        if (result.error) {
          setError(result.error);
          showToast(result.error, 'error');
          setProcessingDatalab(false);
        } else {
          setMarkdownFiles(result.markdownFiles);
          setDatalabTime(result.datalabTime);
          setProcessingDatalab(false);
          showToast(t('step1.toast.convertSuccess', { count: result.markdownFiles.length }), 'success');
        }
      }).catch((err) => {
        console.error('Datalab promise error:', err);
        setError(err.message || t('step1.toast.convertFailed'));
        showToast(err.message || t('step1.toast.convertFailed'), 'error');
        setProcessingDatalab(false);
      });
    }
  }, [datalabPromise, showToast, t]);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  // Handle checkbox selection from predefined list
  const handleCheckboxChange = (attachment) => {
    setFormData(prev => {
      const attachments = prev.attachments.includes(attachment)
        ? prev.attachments.filter(a => a !== attachment)
        : [...prev.attachments, attachment];
      return { ...prev, attachments };
    });
    setErrors(prev => ({ ...prev, attachments: '' }));
  };

  // Add custom attachment
  const handleAddCustomAttachment = async () => {
    const ruleText = customAttachment.trim();
    if (!ruleText) return;

    if (formData.attachments.includes(ruleText)) {
      setErrors(prev => ({ ...prev, custom: 'هذا الشرط موجود مسبقاً' }));
      showToast('هذا الشرط موجود مسبقاً', 'error');
      return;
    }

    try {
      // Save to backend rules.json
      const response = await axios.post(`${API_URL}/rules`, { rule: ruleText });
      // Update the predefined list from the response
      setPredefinedAttachments(response.data);

      // Also add it to the selected attachments for the current form
      setFormData(prev => ({
        ...prev,
        attachments: [...prev.attachments, ruleText]
      }));

      setCustomAttachment('');
      setErrors(prev => ({ ...prev, custom: '', attachments: '' }));
      showToast('تمت إضافة الشرط بنجاح', 'success');
    } catch (err) {
      console.error('Error adding rule:', err);
      const msg = err.response?.data?.error || 'فشل في حفظ الشرط';
      setErrors(prev => ({ ...prev, custom: msg }));
      showToast(msg, 'error');
    }
  };

  // Remove attachment
  const handleRemoveAttachment = (attachment) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter(a => a !== attachment)
    }));
  };

  // Validate form
  const validate = () => {
    const newErrors = {};

    if (!formData.requestId.trim()) {
      newErrors.requestId = t('step2.validation.civilIdRequired');
      showToast(t('step2.validation.civilIdRequired'), 'error');
    }

    if (!formData.civilId.trim()) {
      newErrors.civilId = t('step2.validation.civilIdRequired');
      showToast(t('step2.validation.civilIdRequired'), 'error');
    } else if (!/^\d{10}$/.test(formData.civilId.trim())) {
      newErrors.civilId = t('step2.validation.civilIdInvalid');
      showToast(t('step2.validation.civilIdInvalid'), 'error');
    }

    if (formData.attachments.length === 0) {
      newErrors.attachments = t('step2.validation.attachmentsRequired');
      showToast(t('step2.validation.attachmentsRequired'), 'error');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle next step - navigate to Step 3 (API calls will happen there)
  const handleNext = () => {
    if (!validate()) {
      return;
    }

    // Allow navigation even if Datalab is still processing
    // Step 3 will handle waiting for Datalab and then calling OpenRouter/OpenAI
    onNext({
      datalabPromise: datalabPromise,
      requestData: formData,
      datalabTime: datalabTime || 0
    });
  };

  // Handle Select All
  const handleSelectAll = () => {
    const isAllSelected = predefinedAttachments.every(a => formData.attachments.includes(a));

    setFormData(prev => {
      // Keep custom attachments
      const customAttachments = prev.attachments.filter(a => !predefinedAttachments.includes(a));

      const newAttachments = !isAllSelected
        ? [...customAttachments, ...predefinedAttachments]
        : customAttachments;

      return { ...prev, attachments: newAttachments };
    });
  };

  const isAllSelected = predefinedAttachments.length > 0 && predefinedAttachments.every(a => formData.attachments.includes(a));
  const totalAttachments = predefinedAttachments.length + (formData.attachments.filter(a => !predefinedAttachments.includes(a)).length);
  const selectedCount = formData.attachments.length;

  // Show loading screen when validating
  if (validating) {
    return (
      <div className="step-container">
        <div className="loading-container">
          <div className="loader-con">
            <div style={{ '--i': 0 }} className="pfile"></div>
            <div style={{ '--i': 1 }} className="pfile"></div>
            <div className="pfile" style={{ '--i': 2 }}></div>
            <div className="pfile" style={{ '--i': 3 }}></div>
            <div className="pfile" style={{ '--i': 4 }}></div>
            <div className="pfile" style={{ '--i': 5 }}></div>
          </div>
          <p className="loading-text">{t('step2.loadingText')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="step-container">
      <div className="step-header">
        <h2>{t('step2.title')}</h2>
        <p className="step-description">{t('step2.description')}</p>
      </div>

      {/* Datalab Processing Indicator */}
      {processingDatalab && (
        <div className="datalab-processing-indicator">
          <div className="processing-indicator-content">
            <div className="processing-spinner"></div>
            <span>{t('step1.loadingText')}</span>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && <div className="alert alert-error">{error}</div>}

      {/* Form Card */}
      <div className="form-card">
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="civilId">{t('step2.civilId')}</label>
            <input
              type="text"
              id="civilId"
              name="civilId"
              value={formData.civilId}
              onChange={handleInputChange}
              className={errors.civilId ? 'error' : ''}
              placeholder={t('step2.civilIdPlaceholder')}
              maxLength="10"
            />
            {errors.civilId && <span className="error-message">{errors.civilId}</span>}
          </div>
          <div className="form-group">
            <label htmlFor="requestId">{t('step3.deedNumber')}</label>
            <input
              type="text"
              id="requestId"
              name="requestId"
              value={formData.requestId}
              onChange={handleInputChange}
              className={errors.requestId ? 'error' : ''}
              placeholder={t('step2.requestIdPlaceholder')}
            />
            {errors.requestId && <span className="error-message">{errors.requestId}</span>}
          </div>
        </div>
      </div>

      {/* Requirements Layout */}
      <div className="requirements-layout">

        {/* Right Content: Building Requirements */}
        <div className="requirements-card">
          <div className="requirements-header">
            <h3>{t('step2.attachments')}</h3>
            <button onClick={handleSelectAll} className="select-all-btn">
              {isAllSelected ? (lang === 'ar' ? 'إلغاء تحديد الكل' : 'Deselect All') : (lang === 'ar' ? 'تحديد الكل' : 'Select All')}
            </button>
          </div>

          <div className="search-box">
            <button
              type="button"
              onClick={handleAddCustomAttachment}
              className="add-btn"
            >
              +
            </button>
            <input
              type="text"
              value={customAttachment}
              onChange={(e) => {
                setCustomAttachment(e.target.value);
                setErrors(prev => ({ ...prev, custom: '' }));
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddCustomAttachment();
                }
              }}
              placeholder={t('step2.customAttachmentPlaceholder')}
            />
          </div>
          {errors.custom && <p className="error-message" style={{ marginBottom: '1rem' }}>{errors.custom}</p>}

          <div className="requirements-grid">
            {loading ? (
              <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '2rem' }}>{t('common.loading')}</div>
            ) : predefinedAttachments.map((attachment) => (
              <div
                key={attachment}
                className={`requirement-item ${formData.attachments.includes(attachment) ? 'selected' : ''}`}
                onClick={() => handleCheckboxChange(attachment)}
              >
                <div className="req-info">
                  <span className="info-icon">ⓘ</span>
                  <span>{attachment}</span>
                </div>
                <input
                  type="checkbox"
                  checked={formData.attachments.includes(attachment)}
                  readOnly
                  className="req-checkbox"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Left Sidebar: Selected Requirements */}
        <div className="selected-sidebar">
          <div className="selected-header">
            <h3>{lang === 'ar' ? 'الاشتراطات المختارة' : 'Selected Requirements'}</h3>
            <div className="count-badge">{selectedCount}</div>
          </div>

          <div className="selected-tags">
            {formData.attachments.length > 0 ? (
              formData.attachments.map((attachment, index) => (
                <div key={index} className="selected-tag">
                  <span>{attachment}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveAttachment(attachment)}
                    className="remove-tag"
                  >
                    ×
                  </button>
                </div>
              ))
            ) : (
              <p style={{ color: '#9CA3AF', textAlign: 'center', fontSize: '0.875rem' }}>
                {lang === 'ar' ? 'لم يتم اختيار أي اشتراطات' : 'No requirements selected'}
              </p>
            )}
          </div>

          <div className="progress-summary">
            <div className="progress-text">
              <span>{lang === 'ar' ? 'الإجمالي الاشتراطات المختارة' : 'Total Selected Requirements'}</span>
              <span>{selectedCount} {t('step3.of')} {totalAttachments || 10}</span>
            </div>
            <div className="progress-bar-bg">
              <div
                className="progress-bar-fill"
                style={{ width: `${Math.min(100, (selectedCount / (totalAttachments || 10)) * 100)}%` }}
              ></div>
            </div>
          </div>

          <button
            onClick={handleNext}
            className="btn-next"
            disabled={formData.attachments.length === 0 || validating}
          >
            {t('step2.nextButton')}
          </button>
        </div>

      </div>
    </div>
  );
}

export default Step2Rules;
